window["ThemeComponent_ProductTile"] = ({ product, variant, cart_qty }) => {
  return {
    productForms: null,
    productRoot: null,
    product: product,
    current_variant: variant,
    loading: false,
    tileLoading: false,
    quantity: 1,
    cart_qty: cart_qty,
    total_qty: 0,
    addedToCart: false,
    cartAddErrorMessage: null,
    activeSiblingId: product ? String(product.id) : null,
    activeVariantMediaId: null,
    get currentVariantId() {
      if (this.current_variant) {
        return this.current_variant.id;
      } else {
        return null;
      }
    },
    get currentVariantAvailable() {
      if (this.current_variant) {
        return this.current_variant.available;
      } else {
        return null;
      }
    },
    get current_price() {
      return this.current_variant.price;
    },
    formatMoney(price, moneyFormat = theme.defaultMoneyFormat) {
      return formatMoney(price, moneyFormat);
    },
    /**
     * Refresh the entire tile by fetching the product-tile section for the
     * given product URL via the Section Rendering API, then replacing this
     * component's root element with the newly fetched `.product-tile-inner`.
     * Alpine auto-initialises the new element via its MutationObserver.
     *
     * @param {string} url - Product URL, optionally with a ?variant=<id> param.
     */
    async refreshTile(url) {
      if (this.tileLoading) return;

      const el = this.$root;
      this.tileLoading = true;

      try {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set("section_id", "product-tile");

        const res = await fetch(urlObj.toString());
        const html = await res.text();
        const newInner = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(".product-tile-inner");

        if (newInner) {
          el.replaceWith(newInner);
          document.body.dispatchEvent(
            new CustomEvent("switch:load:images", { bubbles: true }),
          );
        } else {
          this.tileLoading = false;
        }
      } catch (err) {
        console.error("[product-tile] Tile refresh failed:", err);
        this.tileLoading = false;
      }
    },
    selectVariantSwatch(el, mediaId) {
      const variantId = parseInt(el.dataset.variantId);
      if (variantId) {
        const fullVariant = this.product.variants?.find(
          (v) => v.id === variantId,
        );
        if (fullVariant) this.current_variant = fullVariant;
      }
      this.activeVariantMediaId = mediaId ? String(mediaId) : null;
      this.$nextTick(() => {
        document.body.dispatchEvent(
          new CustomEvent("switch:load:images", { bubbles: true }),
        );
      });
    },
    initCarouselIndicator() {
      const track = this.$root.querySelector(".tile-gallery");
      if (!track) return;

      const indicator = this.$root.querySelector(".carousel-indicator");
      if (!indicator) return;

      const thumb = indicator.querySelector(".carousel-indicator__thumb");

      const update = () => {
        const { scrollLeft, scrollWidth, clientWidth } = track;
        const maxScroll = scrollWidth - clientWidth;
        const scrollable = maxScroll > 1;

        indicator.hidden = !scrollable;
        if (!scrollable) return;

        const thumbWidthPct = (clientWidth / scrollWidth) * 100;
        const thumbLeftPct = (scrollLeft / maxScroll) * (100 - thumbWidthPct);
        thumb.style.width = thumbWidthPct + "%";
        thumb.style.left = thumbLeftPct + "%";
      };

      track.addEventListener("scroll", update, { passive: true });

      const ro = new ResizeObserver(update);
      ro.observe(track);

      update();
    },
    init() {
      // Set a product root for nested components
      // to use instead of $root (which refers to their root)
      this.productRoot = this.$root;

      this.initCarouselIndicator();

      this.total_qty = this.cart_qty + this.quantity;

      window.addEventListener("switch:modalcart:cartqtychange", (e) => {
        if (!e.detail.response) return;

        // Qty was updated in cart
        if (e.detail.response.item_count !== 0) {
          const isVariant = e.detail.response.items.find(
            (item) => item.variant_id === this.current_variant.id,
          );
          if (isVariant && isVariant.id === this.current_variant.id) {
            // The variant is still in cart
            this.cart_qty = isVariant.quantity;
          } else {
            // The variant was removed from cart, qty is 0
            this.cart_qty = 0;
          }
        } else {
          // All items were removed from cart
          this.cart_qty = 0;
        }
        this.total_qty = this.cart_qty + this.quantity;
      });

      const formEl = this.$root.querySelector(".quick-buy-product-form");

      if (formEl !== null) {
        formEl.addEventListener("submit", this.submitForm.bind(this));
      }

      //check if window is /cart page
      this.isCartPage = window.location.pathname === "/cart";
    },
    dispatchInputChange() {
      this.$refs.quantityInput.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
      this.total_qty = this.cart_qty + this.quantity;
    },
    decreaseQuantity() {
      this.quantity > 1 && this.quantity--;
      this.dispatchInputChange();
    },
    increaseQuantity() {
      this.quantity++;
      this.dispatchInputChange();
    },
    submitForm(evt) {
      evt.preventDefault();
      this.loading = true;
      this.addedToCart = false;
      this.cartAddErrorMessage = null;

      liveRegion(window.theme.strings.loading);

      const formData = new FormData(evt.target);
      const formId = evt.target.getAttribute("id");

      let modalCart = !this.isCartPage;

      const config = fetchConfigDefaults("application/javascript");

      if (modalCart) {
        formData.append(
          "sections",
          "drawer-cart-items,drawer-cart-footer,cart-item-count,cart-items-count",
        );
      } else if (this.isCartPage) {
        formData.append(
          "sections",
          "cart-items,cart-footer,cart-item-count,cart-items-count",
        );
      } else {
        formData.append("sections", "cart-item-count,cart-items-count");
      }

      formData.append("sections_url", window.location.pathname);

      config.body = formData;

      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            this.loading = false;

            let errors = data.errors || data.description;
            let message = data.description || data.message;

            document.body.dispatchEvent(
              new CustomEvent("switch:productform:adderror", {
                detail: {
                  source: "product-form",
                  sourceId: formId,
                  variantId: formData.get("id"),
                  errors,
                  message,
                },
              }),
            );

            return;
          }

          this.loading = false;
          this.addedToCart = true;
          this.cart_qty = this.quantity;
          this.total_qty = this.cart_qty + this.quantity;

          const afterAddEvent = new CustomEvent("switch:cart:afteradditem", {
            bubbles: true,
            detail: { response: data },
          });
          document.body.dispatchEvent(afterAddEvent);

          if (!document.querySelector('[data-show-on-add="true"]')) {
            if (this.$refs.added)
              this.$nextTick(() => this.$refs.added.focus());
          }
        })
        .catch((error) => {
          if (typeof error === "string") {
            alert(error);
          } else {
            this.loading = false;

            if (error.json()) {
              error.json().then((a) => {
                this.loading = false;
                alert(a.description);
              });
            } else {
              console.error(error);
            }
          }
        });
    },
  };
};
